/**
 * The single conversation surface of the simplified landing, built from the NextAdmin demo `/chats`: a header row, the
 * thread (or the starter cards while there is no conversation yet), the composer at the bottom. The thread is the same
 * `ConversationChat` the detailed page uses, with the `simple` surface.
 *
 * Every affordance inside the stream is a card that SAYS something back; the `SayProvider` routes those messages
 * through this surface's own turn pipeline instead of letting cards call APIs.
 */
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import React, { useEffect } from 'react';
import { ConversationChat } from '../../components/conversations/ConversationChat.js';
import { ConversationMembersButton } from '../../components/conversations/ConversationMembersButton.js';
import { SayProvider } from '../../components/conversations/cards/SayContext.js';
import { RuntimePolicyMenu } from '../../components/ui/RuntimePolicyMenu.js';
import { Button } from '../../components/ui/Button.js';
import { Chip } from '../../components/ui/Chip.js';
import { StatusDot } from '../../components/ui/StatusDot.js';
import { getConversationOptions } from '../../generated/api/@tanstack/react-query.gen.js';
import { RuntimePolicySurface } from '../../generated/api/types.gen.js';
import { useConversationStream } from '../../hooks/useConversationStream.js';
import { EntityOpenerProvider } from '../../components/conversations/entities/EntityOpenerProvider.js';
import { OpenConversationContext } from '../../components/conversations/open-conversation-context.js';
import { SimpleHelpButton } from './SimpleHelpButton.js';
import { SimpleRecordsButton } from './SimpleRecordsButton.js';
import { SimpleRecordsLayout } from './SimpleRecordsLayout.js';
import { copyFor } from './simpleModuleCopy.js';
import { markTurnCompleted, markTurnStarted } from './simpleTurnActivity.js';

interface Props {
  activeModuleId: string;
  conversationId: string | null;
  /** Starts a brand-new conversation from `message` (and the files it carries) and selects it. */
  onStartConversation: (message: string, attachments?: number[]) => void;
  starting: boolean;
  /** Policy chosen for the NEXT conversation (before its first message); null = the default. */
  runtimePolicyId: number | null;
  onRuntimePolicyChange: (policyId: number) => void;
  /** Opens another conversation in place of this one (a member's thread, or back to the conversation it serves). */
  onOpenConversation: (conversationId: string) => void;
}

/**
 * Connectivity, in the user's terms. The distinction that matters to them is whether replies arrive by themselves
 * ("Live") or only when the page re-asks ("Polling") — the transport name is an implementation detail, the
 * consequence is not.
 */
function connectionState(
  conversationId: string | null,
  status: ReturnType<typeof useConversationStream>['eventsState']['status'],
): { variant: 'success' | 'warning' | 'neutral'; label: string } {
  if (!conversationId) return { variant: 'neutral', label: 'Not connected' };
  switch (status) {
    case 'open':
      return { variant: 'success', label: 'Live' };
    case 'connecting':
      return { variant: 'warning', label: 'Connecting…' };
    case 'reconnecting':
      return { variant: 'warning', label: 'Reconnecting…' };
    case 'error':
      return { variant: 'warning', label: 'Polling' };
    default:
      return { variant: 'neutral', label: 'Idle' };
  }
}

export const SimpleConversation: React.FC<Props> = ({
  activeModuleId,
  conversationId,
  onStartConversation,
  starting,
  runtimePolicyId,
  onRuntimePolicyChange,
  onOpenConversation,
}) => {
  const copy = copyFor(activeModuleId);
  const stream = useConversationStream(conversationId, 'simple');

  const detail = useQuery({
    ...getConversationOptions({ path: { id: conversationId ?? '' } }),
    enabled: Boolean(conversationId),
  });

  // Cards inside the stream say things back through the same pipeline as the composer.
  const say = (message: string) => {
    if (conversationId) stream.send(message);
    else onStartConversation(message);
  };

  // Mirror the live turn into the rail's dot state (push-based, not a query).
  const agentThinking = stream.eventsState.agentThinking;
  useEffect(() => {
    if (!conversationId) return;
    if (agentThinking) markTurnStarted(conversationId);
    else markTurnCompleted(conversationId);
    return () => markTurnCompleted(conversationId);
  }, [conversationId, agentThinking]);

  // A member's thread names the conversation it works for; its header leads back there.
  const parentId = detail.data?.parentConversationId ?? null;
  const parent = useQuery({
    ...getConversationOptions({ path: { id: String(parentId ?? '') } }),
    enabled: parentId !== null,
  });

  const title = detail.data?.topic || copy.title;
  const connection = connectionState(conversationId, stream.eventsState.status);

  const empty = (
    <div className="mx-auto w-full max-w-[820px]">
      <h2 className="mb-2 text-3xl font-semibold tracking-tight text-heading">What do you want to build today?</h2>
      <p className="mb-6 max-w-[560px] text-base text-muted">
        Tell me the idea. I will create the project, pull in the right specialists, run the tests and come back with
        numbers and a recommendation — you only decide.
      </p>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(230px,1fr))] gap-3">
        {copy.starters.map((starter) => (
          <Button
            key={starter.label}
            variant="secondary"
            disabled={starting}
            onClick={() => onStartConversation(starter.message)}
            className="h-auto flex-col items-start justify-start gap-1.5 whitespace-normal p-3.5 text-left"
          >
            <span className="font-mono text-[10px] uppercase tracking-wider text-accent-text">{starter.kind}</span>
            <span className="text-sm font-semibold text-heading">{starter.label}</span>
            <span className="text-xs font-normal text-muted">{starter.description}</span>
          </Button>
        ))}
      </div>
    </div>
  );

  const newestMessageId = stream.messages.at(-1)?.id ?? null;

  return (
    // A record a card shows opens in the "In this conversation" column; a member's thread opens in this place.
    <OpenConversationContext value={onOpenConversation}>
      <EntityOpenerProvider>
        <main className="flex min-h-0 min-w-0 flex-col">
          <header className="flex min-w-0 shrink-0 flex-wrap items-center gap-3 border-b px-7 py-3.5">
            {parentId !== null && (
              <Button variant="ghost" size="sm" onClick={() => onOpenConversation(String(parentId))}>
                <ArrowLeft size={14} aria-hidden />
                {parent.data?.topic ? `Back to ${parent.data.topic}` : 'Back to the conversation'}
              </Button>
            )}
            <h1 className="m-0 text-base font-semibold text-heading">{title}</h1>
            {detail.isError && (
              // The thread still works; only its title and policy are missing.
              <Button variant="ghost" size="sm" onClick={() => void detail.refetch()}>
                Conversation details did not load. Retry
              </Button>
            )}
            <span className="flex-1" />
            {conversationId && <SimpleRecordsButton conversationId={conversationId} />}
            {conversationId && (
              <ConversationMembersButton
                conversationId={conversationId}
                messages={stream.messages}
                onOpenConversation={onOpenConversation}
              />
            )}
            <Chip tone="neutral" icon={<StatusDot variant={connection.variant} size="sm" />} className="font-sans">
              <span
                title={
                  connection.variant === 'success'
                    ? 'Replies arrive as they are written'
                    : 'Replies arrive on refresh rather than live'
                }
              >
                {connection.label}
              </span>
            </Chip>
            <SimpleHelpButton />
          </header>

          <div className="min-h-0 flex-1">
            <SimpleRecordsLayout conversationId={conversationId} newestMessageId={newestMessageId}>
              <SayProvider say={say}>
                <ConversationChat
                  key={conversationId ?? 'new'}
                  conversationId={conversationId}
                  surface="simple"
                  stream={conversationId ? stream : undefined}
                  onStart={onStartConversation}
                  attachments
                  starting={starting}
                  empty={empty}
                  // The module's own invitation is said once, to a blank page; from the first message on it is the neutral one.
                  placeholder={stream.messages.length === 0 ? copy.placeholder : 'Write a message…'}
                  policyControl={
                    conversationId ? undefined : (
                      <RuntimePolicyMenu
                        surface={RuntimePolicySurface.CONVERSATION}
                        value={runtimePolicyId}
                        onChange={onRuntimePolicyChange}
                        disabled={starting}
                        placement="above"
                      />
                    )
                  }
                />
              </SayProvider>
            </SimpleRecordsLayout>
          </div>
        </main>
      </EntityOpenerProvider>
    </OpenConversationContext>
  );
};
